import React, { forwardRef, useState } from 'react'
import PropTypes from 'prop-types'

/**
 * Wraps a Build section form with shared save plumbing.
 *
 * @param {React.ComponentType} Component
 * @param {{ lockMode?: 'hard' | 'queue' }} [hocOptions]
 *   - queue (default): keep fields editable while save API runs; sections
 *     should follow up with runQueuedFormikSave when using Formik
 *   - hard: disable the whole fieldset while a save API is in flight
 */
const QuestHOC = (Component, hocOptions = {}) => {
  const lockMode = hocOptions.lockMode || 'queue'
  const useHardLock = lockMode === 'hard'

  const WrappedComponent = forwardRef(function QuestHOCWrapped(props, ref) {
    const [isFormSubmitting, setIsFormSubmitting] = useState(false)
    const busy = !!isFormSubmitting || !!props.isSaving

    const updateQuestDetails = async (values, options = null) => {
      const requestBody = {
        id: props.questDetail.id || props.questDetail._id,
        ...values,
      }
      try {
        setIsFormSubmitting(true)
        // Second arg: legacy string key, or options object { silent: true }
        await props.addUpdateQuest(requestBody, options)
        setIsFormSubmitting(false)
      } catch (e) {
        console.error(e)
        setIsFormSubmitting(false)
      }
    }

    return (
      <fieldset
        disabled={useHardLock ? busy : false}
        aria-busy={busy}
        data-autosave-lock={lockMode}
        className={`experience-section-fieldset${busy ? ' is-saving' : ''}`}>
        <Component
          {...props}
          ref={ref}
          updateQuestDetails={updateQuestDetails}
          isFormSubmitting={busy}
          isSaving={!!props.isSaving}
          autosaveLockMode={lockMode}
        />
      </fieldset>
    )
  })

  WrappedComponent.displayName = `QuestHOC(${
    Component.displayName || Component.name || 'Component'
  })`
  WrappedComponent.propTypes = {
    addUpdateQuest: PropTypes.func,
    questDetail: PropTypes.object,
    isSaving: PropTypes.bool,
  }
  return WrappedComponent
}

export default QuestHOC
